import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { useBranches } from '@/Pages/Operations/shared';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import { apiError, TimeEntryStatus, type QueryTimeEntriesParams, type TimeEntry } from './lib';
import TimesheetDetailDialog from './TimesheetDetailDialog';

/** Timesheets, derived from punches rather than accumulated by hand — a
 * mis-posted punch self-heals on the next read instead of skewing a figure
 * forever, the same reasoning `CashierShiftsService` uses for a drawer's
 * `expectedCash`. */
export default function TimesheetsTab() {
  const { t, i18n } = useTranslation();
  const branches = useBranches();

  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState<TimeEntryStatus | ''>(TimeEntryStatus.PENDING_APPROVAL);
  const [rows, setRows] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: QueryTimeEntriesParams = {
      limit: 100,
      branchId: branchId || undefined,
      status: status || undefined,
    };
    api
      .get<{ data: TimeEntry[] }>('/attendance/entries', { params })
      .then(({ data }) => setRows(data.data ?? []))
      .catch((e) => setError(apiError(e, t('workforce.timesheets.loadFailed'))))
      .finally(() => setLoading(false));
  }, [branchId, status, t]);

  useEffect(load, [load]);

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="flex flex-wrap gap-3 p-4">
          {branches.length > 0 && (
            <Field label={t('workforce.timesheets.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('workforce.timesheets.anyBranch')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t('workforce.timesheets.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value as TimeEntryStatus | '')}>
              <option value="">{t('workforce.timesheets.anyStatus')}</option>
              {Object.values(TimeEntryStatus).map((s) => (
                <option key={s} value={s}>
                  {t(`workforce.timeEntryStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {loading ? (
        <LoadingState label={t('common.loading')} />
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={t(status === TimeEntryStatus.PENDING_APPROVAL ? 'workforce.timesheets.emptyPending' : 'workforce.timesheets.empty')}
          hint={t('workforce.timesheets.emptyHint')}
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('workforce.timesheets.employee')}</TH>
                <TH>{t('workforce.timesheets.branch')}</TH>
                <TH>{t('workforce.timesheets.businessDate')}</TH>
                <TH className="text-end">{t('workforce.timesheets.worked')}</TH>
                <TH>{t('workforce.timesheets.status')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <TR key={row.id} className="cursor-pointer hover:bg-hover" onClick={() => setOpenId(row.id)}>
                  <TD className="font-medium">
                    {row.employee.firstName} {row.employee.lastName ?? ''}
                  </TD>
                  <TD>{row.branch.name}</TD>
                  <TD>{formatDateTime(row.businessDate, i18n.language)}</TD>
                  <TD className="text-end tabular-nums">{t('workforce.timesheets.minutes', { count: row.workedMinutes })}</TD>
                  <TD>
                    <Badge tone={toneForStatus(row.status)}>{t(`workforce.timeEntryStatus.${row.status}`)}</Badge>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {openId && (
        <TimesheetDetailDialog entryId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      )}
    </div>
  );
}
